<template>
  <div>
    <!-- SSR'de hemen render etmemiz gereken placement içerikleri -->
    <div v-if="ssrSections.length" class="max-w-7xl mx-auto md:px-8 my-6 space-y-6">
      <FrontendProductCarouselSection
        v-for="section in ssrSections"
        :key="section.id"
        :title="section.title"
        :products="section.products"
        :loading="carouselsPending"
        :view-all-link="section.viewAllLink"
        :banner="section.banner"
        :banners="section.banners"
        :banner-alt="section.title"
        @add-to-cart="(payload) => emit('add-to-cart', payload)"
        @toggle-favorite="(payload) => emit('toggle-favorite', payload)"
      />
    </div>

    <!-- SSR sonrasında istemciye bırakılan bölüm -->
    <div class="max-w-7xl mx-auto md:px-8 my-6 space-y-6">
      <ClientOnly v-for="carousel in clientCarousels" :key="carousel.id">
        <Suspense>
          <DeferredProductCarousel
            :title="carousel.name"
            :fetcher="() => fetchCarouselItems(carousel.id)"
            :view-all-link="carousel.view_all_url"
            :banner="carousel.banner || null"
            :banners="carousel.banners || []"
            :banner-alt="carousel.name"
            @add-to-cart="(payload) => emit('add-to-cart', payload)"
            @toggle-favorite="(payload) => emit('toggle-favorite', payload)"
          />
          <template #fallback>
            <FrontendProductCarouselSection
              :title="carousel.name"
              :products="[]"
              :loading="true"
              :view-all-link="carousel.view_all_url"
              :banner="carousel.banner || null"
              :banners="carousel.banners || []"
              :banner-alt="carousel.name"
            />
          </template>
        </Suspense>
      </ClientOnly>
    </div>
  </div>
</template>

<script setup>

const DeferredProductCarousel = defineAsyncComponent(() =>
  import('~/components/Frontend/Product/Carousel/DeferredProducts.client.vue')
)

const props = defineProps({
  placement: {
    type: String,
    required: true
  },
  location: {
    type: String,
    default: 'homepage'
  },
  itemsLimit: {
    type: Number,
    default: 12
  },
  ssrLimit: {
    type: Number,
    default: 1
  },
  categoryId: {
    type: [Number, String, null],
    default: null
  }
})

const emit = defineEmits(['add-to-cart', 'toggle-favorite'])

const normalizeId = (value) => {
  const numeric = Number(value)
  return Number.isNaN(numeric) ? null : numeric
}

const normalizedCategoryId = computed(() => normalizeId(props.categoryId))

const carouselParams = computed(() => {
  const params = {
    location: props.location,
    placement: props.placement
  }

  const categoryId = normalizedCategoryId.value
  if (categoryId !== null) {
    params.category_id = categoryId
  }

  return params
})

const {
  carousels,
  pending: carouselsPending,
  getCarouselItems
} = await useCarousels({
  key: `placement-orchestrator-${props.location}-${props.placement}-${normalizedCategoryId.value ?? 'none'}`,
  params: carouselParams,
  watch: [() => props.location, () => props.placement, normalizedCategoryId]
})

const ssrSections = ref([])

const normalizeProducts = (value) => {
  if (Array.isArray(value)) {
    return value
  }

  if (value && typeof value === 'object') {
    if (Array.isArray(value.data)) {
      return value.data
    }

    const collection = value.data?.['Illuminate\\Support\\Collection']
    if (Array.isArray(collection)) {
      return collection
    }
  }

  return []
}

const buildSsrSections = async () => {
  ssrSections.value = []

  if (props.ssrLimit <= 0) {
    return
  }

  const list = (carousels.value ?? []).slice(0, props.ssrLimit)

  for (const carousel of list) {
    const { items } = await useCarouselItems(carousel.id, {
      key: `placement-orchestrator-items-${carousel.id}`,
      defaultValue: [],
      query: {
        limit: props.itemsLimit,
        category_id: normalizedCategoryId.value
      },
      immediate: true
    })

    const products = normalizeProducts(items.value)

    if (products.length === 0) {
      continue
    }

    ssrSections.value.push({
      id: carousel.id,
      title: carousel.name,
      products,
      viewAllLink: carousel.view_all_url,
      banner: carousel.banner || null,
      banners: carousel.banners || []
    })
  }
}

await buildSsrSections()

watch(
  [() => carousels.value, normalizedCategoryId],
  () => {
    buildSsrSections()
  }
)

const clientCarousels = computed(() =>
  (carousels.value ?? []).slice(props.ssrLimit)
)

const fetchCarouselItems = (carouselId) =>
  getCarouselItems(carouselId, {
    limit: props.itemsLimit,
    category_id: normalizedCategoryId.value
  }).then(normalizeProducts)
</script>
